import { ActionTree } from 'vuex';
import {
  connectMessagingClient,
  disconnectMessagingClient,
  fetchGroupChannels,
  fetchChannelWithMessages,
  sendChannelMessage,
  fetchChannelByUrl,
} from '@/apps/messages/helpers/sendbirdClient';
import { mapChannelToPreview, mapMessageToItem } from '@/apps/messages/helpers/messageMapper';
import { getOtherUserInformation } from '@/apps/messages/helpers/otherUserInfo';
import { getLastMessageDate, formatMessageTime } from '@/apps/messages/helpers/channelPreview';
import type { Account } from '@/models/account';
import { MessagesState } from './types';

const actions: ActionTree<MessagesState, any> = {
  setCurrentChannelUrl({ commit }, url: string | null) {
    commit('selectChannel', url);
  },
  setConnectionError({ commit }, value: string | null) {
    commit('setConnectionError', value);
  },
  handleMessageReceived(
    { commit, state },
    payload: {
      messageId: number | null;
      createdAt: number | null;
      channelUrl: string | null;
      isFromCurrentUser?: boolean;
      messageText?: string;
    }
  ) {
    if (payload.isFromCurrentUser) return;

    commit('setLastReceivedMessage', payload);

    if (payload.channelUrl) {
      if (payload.channelUrl !== state.currentChannelUrl) {
        commit('incrementChannelUnreadCount', payload.channelUrl);

        // Fetch the channel to get fresh lastMessage data
        fetchChannelByUrl(payload.channelUrl)
          .then((freshChannel: any) => {
            const freshMessage = freshChannel.lastMessage as any;

            const channelPreview = state.channels.find((ch: any) => ch.url === payload.channelUrl);
            if (channelPreview) {
              const lastMessageAt = channelPreview.otherUser
                ? getLastMessageDate(channelPreview.otherUser, freshMessage)
                : formatMessageTime(freshMessage?.createdAt);

              commit('updateChannelPreviewAndSort', {
                channelUrl: payload.channelUrl,
                lastMessage: freshMessage?.message || '',
                lastMessageAt,
              });
            }
          })
          .catch(() => {
            // Fallback: use the message data we have
            if (payload.createdAt) {
              const channelPreview = state.channels.find((ch: any) => ch.url === payload.channelUrl);
              if (channelPreview) {
                const lastMessageAt = channelPreview.otherUser
                  ? getLastMessageDate(channelPreview.otherUser, { createdAt: payload.createdAt })
                  : formatMessageTime(payload.createdAt);

                commit('updateChannelPreviewAndSort', {
                  channelUrl: payload.channelUrl,
                  lastMessage: payload.messageText || '',
                  lastMessageAt,
                });
              }
            }
          });
      } else if (payload.createdAt) {
        // Still update preview with payload data if we have it
        const channelPreview = state.channels.find((ch: any) => ch.url === payload.channelUrl);
        if (channelPreview) {
          const lastMessageAt = channelPreview.otherUser
            ? getLastMessageDate(channelPreview.otherUser, { createdAt: payload.createdAt })
            : formatMessageTime(payload.createdAt);

          commit('updateChannelPreviewAndSort', {
            channelUrl: payload.channelUrl,
            lastMessage: payload.messageText || '',
            lastMessageAt,
          });
        }
      }
    }
  },

  handleReadReceiptUpdated({ commit, state }, channel: any) {
    if (!channel?.url || channel.url !== state.currentChannelUrl) {
      return;
    }

    const lastChannelMessage = channel.lastMessage;
    if (!lastChannelMessage) return;

    try {
      const unreadMembers = channel.getUnreadMembers(lastChannelMessage, false);
      const isRead = unreadMembers.length === 0;
      const newStatus = isRead ? 'read' : 'delivered';

      commit('updateLastMessageStatus', { messageStatus: newStatus, read: isRead });
    } catch (_error) {
      // Silently fail
    }
  },

  async initializeMessaging({ commit, dispatch, rootGetters, state }) {
    if (state.isConnecting || state.isConnected) return;

    const account = rootGetters['account/getAccount'] as Account | null;
    if (!account?.id) {
      commit('setConnectionError', 'Account is missing. Unable to start messaging.');
      return;
    }

    commit('setConnecting', true);
    commit('setConnectionError', null);

    try {
      await connectMessagingClient({
        accountId: account.id,
        onMessageReceived: (channel, message) => {
          const senderId = (message as any)?.sender?.userId;
          dispatch('handleMessageReceived', {
            channelUrl: channel?.url ?? null,
            messageId: typeof message?.messageId === 'number' ? message.messageId : null,
            createdAt: typeof message?.createdAt === 'number' ? message.createdAt : null,
            isFromCurrentUser: senderId != null && senderId === account.id,
            messageText: (message as any)?.message || '',
          });
        },
        onUnreadMemberStatusUpdated: (channel) => {
          dispatch('handleReadReceiptUpdated', channel);
        },
      });
      commit('setConnected', true);
    } catch (error: any) {
      commit('setConnected', false);
      commit('setConnectionError', error?.message || 'Failed to connect to messaging.');
    } finally {
      commit('setConnecting', false);
    }
  },
  disconnectMessaging({ commit }) {
    disconnectMessagingClient();
    commit('setConnected', false);
    commit('setConnecting', false);
  },

  async fetchChannelList({ commit, rootGetters }) {
    commit('setLoadingChannels', true);
    commit('setChannelListError', '');

    const accountId = rootGetters['account/getAccount']?.id;
    if (!accountId) return;

    try {
      const channels = await fetchGroupChannels();
      const mapped = channels.map((ch: any) => mapChannelToPreview(ch, accountId));
      commit('setChannels', { channels: mapped, rawChannels: channels });
    } catch (_error) {
      commit('setChannelListError', 'Failed to load conversations. Please check your connection.');
    } finally {
      commit('setLoadingChannels', false);
    }
  },

  async selectChannel({ commit, dispatch }, channelUrl: string) {
    if (!channelUrl) {
      commit('selectChannel', '');
      return;
    }

    commit('selectChannel', channelUrl);
    await dispatch('loadMessagesForChannel', channelUrl);
  },

  async loadMessagesForChannel({ commit, rootGetters }, channelUrl: string) {
    if (!channelUrl || !rootGetters['account/getAccount']?.id) return;

    commit('setLoadingMessages', true);
    commit('setMessageLoadError', '');

    try {
      const { channel, messages } = await fetchChannelWithMessages(channelUrl);
      const accountId = rootGetters['account/getAccount'].id;
      const members = channel.members || [];
      const otherUser = getOtherUserInformation(members, accountId);

      commit('setSelectedUserInfo', { otherUser, members });

      const accountType = rootGetters['account/accountType'];
      const sorted = [...messages].sort((a: any, b: any) => (a.createdAt || 0) - (b.createdAt || 0));
      const mapped = sorted.map((msg: any, index: number) =>
        mapMessageToItem(msg, accountId, otherUser, accountType, channel, index === sorted.length - 1)
      );

      commit('setMessages', mapped);
      commit('clearChannelUnreadCount', channelUrl);
    } catch (_error) {
      commit('setMessages', []);
      commit('setMessageLoadError', 'Failed to load messages. Please check your connection.');
    } finally {
      commit('setLoadingMessages', false);
    }
  },

  async sendMessage({ commit, state, rootGetters }, message: string) {
    const channelUrl = state.currentChannelUrl;
    if (!channelUrl) return;

    const accountId = rootGetters['account/getAccount']?.id;
    if (!accountId) return;

    const accountType = rootGetters['account/accountType'];
    const otherUser = state.selectedOtherUser;
    const now = Date.now();
    const optimisticId = `optimistic-${now}`;

    commit('addMessage', {
      id: optimisticId,
      body: message,
      senderName: '',
      senderAvatarUrl: '',
      isOutgoing: true,
      isAdmin: false,
      timestamp: formatMessageTime(now),
      createdAt: now,
      read: false,
      messageStatus: 'sent',
      accountType,
    });
    commit('setSending', true);
    commit('setSendError', '');

    const channelPreview = state.channels.find((ch: any) => ch.url === channelUrl);
    const optimisticLastMessageAt = channelPreview?.otherUser
      ? getLastMessageDate(channelPreview.otherUser, { createdAt: now })
      : formatMessageTime(now);

    commit('updateChannelPreviewAndSort', {
      channelUrl,
      lastMessage: message,
      lastMessageAt: optimisticLastMessageAt,
    });

    try {
      const sentMessage = await sendChannelMessage(channelUrl, message);
      const rawChannel = state.rawChannels.find((c: any) => c.url === channelUrl);
      const mapped = mapMessageToItem(sentMessage, accountId, otherUser, accountType, rawChannel, true);

      commit('replaceOptimisticMessage', { optimisticId, message: mapped });

      const createdAt = (sentMessage as any).createdAt || now;
      const confirmedLastMessageAt = channelPreview?.otherUser
        ? getLastMessageDate(channelPreview.otherUser, { createdAt })
        : formatMessageTime(createdAt);

      commit('updateChannelPreviewAndSort', {
        channelUrl,
        lastMessage: message,
        lastMessageAt: confirmedLastMessageAt,
      });
    } catch (_error) {
      commit('removeOptimisticMessage', optimisticId);
      const errorMsg = (_error as Error)?.message || 'Failed to send. Please check your connection and try again.';
      commit('setSendError', errorMsg);
    } finally {
      commit('setSending', false);
    }
  },
};

export default actions;
